import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { useVendorDirectoryPaths } from "../../hooks/useVendorDirectoryPaths";
import { listVerificationCases } from "../../api/clientVendorDirectoryVerificationApi";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.verification";

const CLOSED_STATUSES = new Set(["completed", "cancelled", "failed", "expired"]);

const STATUS_FILTERS = [
  "",
  "pending",
  "assigned",
  "in_progress",
  "awaiting_vendor_information",
  "awaiting_external_source",
  "ready_for_review",
  "completed",
  "failed",
  "cancelled",
  "expired",
];

function statusBadgeClass(status) {
  if (status === "completed") return "cvd-badge--published";
  if (["failed", "cancelled", "expired"].includes(status)) return "cvd-badge--suspended";
  if (["ready_for_review", "in_progress"].includes(status)) return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function priorityBadgeClass(priority) {
  if (priority === "urgent") return "cvd-badge--suspended";
  if (priority === "high") return "cvd-badge--warning";
  return "cvd-badge--draft";
}

function formatDateTime(value, locale) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(locale, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function isOverdue(item) {
  if (!item?.dueAt) return false;
  if (CLOSED_STATUSES.has(item.status)) return false;
  return new Date(item.dueAt) < new Date();
}

function MetricCard({ label, value }) {
  return (
    <article className="cvd-metric-card">
      <span className="cvd-metric-card__label">{label}</span>
      <strong className="cvd-metric-card__value">{value ?? 0}</strong>
    </article>
  );
}

function CaseStatusBadge({ status, t }) {
  return (
    <span className={`cvd-badge ${statusBadgeClass(status)}`}>
      {t(`${COPY_PREFIX}.status.${status}`, {}, status)}
    </span>
  );
}

function PriorityBadge({ priority, t }) {
  return (
    <span className={`cvd-badge ${priorityBadgeClass(priority)}`}>
      {t(`${COPY_PREFIX}.priority.${priority}`, {}, priority)}
    </span>
  );
}

export default function ClientVendorDirectoryVerificationListPage() {
  const { clientViewId } = useParams();
  const { serviciosBase } = useVendorDirectoryPaths();
  const { activeView } = useOutletContext() || {};
  const { t, locale } = useLocale();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");

  const status = searchParams.get("status") || "";
  const overdue = searchParams.get("overdue") === "true";
  const basePath = `${serviciosBase}/verificacion`;
  const viewName = activeView?.name || "CasaMapa";

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await listVerificationCases(clientViewId, {
        status: status || undefined,
        overdue: overdue || undefined,
      });
      setCases(body.cases || []);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [clientViewId, overdue, status, t]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const open = cases.filter((item) => !CLOSED_STATUSES.has(item.status)).length;
    const overdueCount = cases.filter((item) => isOverdue(item)).length;
    const ready = cases.filter((item) => item.status === "ready_for_review").length;
    return { total: cases.length, open, overdue: overdueCount, ready };
  }, [cases]);

  const updateParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const toggleOverdue = () => {
    updateParam("overdue", overdue ? "" : "true");
  };

  return (
    <div className="page-content cvd-admin cvd-verification">
      <div className="cvd-admin__header">
        <div>
          <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
          <p className="lede">{t(`${COPY_PREFIX}.intro`, { viewName })}</p>
        </div>
        <div className="cvd-admin__actions">
          <button type="button" className="cvd-btn cvd-btn--ghost" onClick={load}>
            {t(`${COPY_PREFIX}.refresh`)}
          </button>
        </div>
      </div>

      {!loading && cases.length > 0 ? (
        <div className="cvd-metric-grid">
          <MetricCard label={t(`${COPY_PREFIX}.kpi.total`)} value={summary.total} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.open`)} value={summary.open} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.overdue`)} value={summary.overdue} />
          <MetricCard label={t(`${COPY_PREFIX}.kpi.ready`)} value={summary.ready} />
        </div>
      ) : null}

      <div className="cvd-duplicate-filters">
        <div className="cvd-duplicate-filters__group">
          <span className="cvd-duplicate-filters__label">{t(`${COPY_PREFIX}.filterStatus`)}</span>
          <div className="cvd-run-filters" role="group" aria-label={t(`${COPY_PREFIX}.filterStatus`)}>
            {STATUS_FILTERS.map((value) => (
              <button
                key={value || "all-status"}
                type="button"
                className={`cvd-run-filter-chip${status === value ? " is-active" : ""}`}
                onClick={() => updateParam("status", value)}
              >
                {value ? t(`${COPY_PREFIX}.status.${value}`) : t(`${COPY_PREFIX}.allStatuses`)}
              </button>
            ))}
          </div>
        </div>

        <div className="cvd-duplicate-filters__group">
          <div className="cvd-run-filters">
            <button
              type="button"
              className={`cvd-run-filter-chip${overdue ? " is-active" : ""}`}
              onClick={toggleOverdue}
            >
              {t(`${COPY_PREFIX}.filterOverdue`)}
            </button>
          </div>
        </div>
      </div>

      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {!loading && errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}

      {!loading && !errorMessage && cases.length === 0 ? (
        <section className="cvd-run-empty">
          <h2>{t(`${COPY_PREFIX}.emptyTitle`)}</h2>
          <p>{t(`${COPY_PREFIX}.emptyHint`)}</p>
        </section>
      ) : null}

      {!loading && !errorMessage && cases.length > 0 ? (
        <div className="cvd-run-list">
          {cases.map((item) => (
            <article key={item.id} className="cvd-run-card cvd-verification-card">
              <div className="cvd-run-card__main">
                <div className="cvd-run-card__head">
                  <div>
                    <h2 className="cvd-verification-card__vendor">{item.vendorName || item.vendorId}</h2>
                    <p className="cvd-run-card__sub">
                      {item.categoryName || "—"} · {t(`${COPY_PREFIX}.caseType.${item.caseType}`, {}, item.caseType)}
                    </p>
                  </div>
                  <div className="cvd-verification-card__badges">
                    <CaseStatusBadge status={item.status} t={t} />
                    <PriorityBadge priority={item.priority || "normal"} t={t} />
                    {isOverdue(item) ? (
                      <span className="cvd-badge cvd-badge--suspended">{t(`${COPY_PREFIX}.overdueBadge`)}</span>
                    ) : null}
                  </div>
                </div>

                <div className="cvd-run-card__stats">
                  <span>
                    {t(`${COPY_PREFIX}.table.due`)}: <strong>{formatDateTime(item.dueAt, locale)}</strong>
                  </span>
                  <span>
                    {t(`${COPY_PREFIX}.table.updated`)}: {formatDateTime(item.updatedAt, locale)}
                  </span>
                </div>
              </div>

              <div className="cvd-run-card__actions">
                <Link className="cvd-btn cvd-btn--primary" to={`${basePath}/${item.id}`}>
                  {t(`${COPY_PREFIX}.openCase`)}
                </Link>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
}
